import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import type { ScreenerInput, ScreenerRow } from "@alpha/shared";
import { api } from "../api";
import { Empty, ErrorBox, Loading, PageHeader, fmtCompact, fmtNum } from "../components/ui";
import { useAsync } from "../useAsync";

const PAGE = 25;

interface FormState {
  sector: string;
  capMin: string;
  capMax: string;
  above: "any" | "yes" | "no";
  rsiMin: string;
  rsiMax: string;
  adxMin: string;
}
const EMPTY: FormState = { sector: "", capMin: "", capMax: "", above: "any", rsiMin: "", rsiMax: "", adxMin: "" };

const num = (s: string): number | undefined => (s.trim() === "" || !Number.isFinite(Number(s)) ? undefined : Number(s));
const billions = (s: string) => (num(s) === undefined ? undefined : num(s)! * 1e9);

export function toScreenerBody(f: FormState, offset: number): ScreenerInput {
  return {
    ...(f.sector ? { sector: f.sector } : {}),
    ...(billions(f.capMin) !== undefined ? { marketCapMin: billions(f.capMin)! } : {}),
    ...(billions(f.capMax) !== undefined ? { marketCapMax: billions(f.capMax)! } : {}),
    ...(f.above !== "any" ? { aboveSma200: f.above === "yes" } : {}),
    ...(num(f.rsiMin) !== undefined ? { rsiMin: num(f.rsiMin)! } : {}),
    ...(num(f.rsiMax) !== undefined ? { rsiMax: num(f.rsiMax)! } : {}),
    ...(num(f.adxMin) !== undefined ? { adxMin: num(f.adxMin)! } : {}),
    take: PAGE,
    offset,
  };
}

export function ScreenerPage() {
  const sectors = useAsync((s) => api.get<{ items: string[] }>("/v1/screener/sectors", s), []);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [applied, setApplied] = useState<{ form: FormState; offset: number }>({ form: EMPTY, offset: 0 });
  const results = useAsync(
    () => api.post<{ items: ScreenerRow[]; nextOffset: number | null }>("/v1/screener", toScreenerBody(applied.form, applied.offset)),
    [applied],
  );

  const set = (k: keyof FormState) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });
  function submit(e: FormEvent) {
    e.preventDefault();
    setApplied({ form, offset: 0 });
  }

  return (
    <section>
      <PageHeader title="Screener" />
      <form className="card filters" onSubmit={submit}>
        <label>
          Sector
          <select value={form.sector} onChange={set("sector")}>
            <option value="">Any</option>
            {(sectors.data?.items ?? []).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label>
          Market cap min (USD bn)
          <input inputMode="decimal" value={form.capMin} onChange={set("capMin")} />
        </label>
        <label>
          Market cap max (USD bn)
          <input inputMode="decimal" value={form.capMax} onChange={set("capMax")} />
        </label>
        <label>
          Price above SMA 200
          <select value={form.above} onChange={set("above")}>
            <option value="any">Any</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </label>
        <label>
          RSI min
          <input inputMode="decimal" value={form.rsiMin} onChange={set("rsiMin")} />
        </label>
        <label>
          RSI max
          <input inputMode="decimal" value={form.rsiMax} onChange={set("rsiMax")} />
        </label>
        <label>
          ADX min
          <input inputMode="decimal" value={form.adxMin} onChange={set("adxMin")} />
        </label>
        <div className="row end">
          <button type="submit" className="btn primary">
            Run screen
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => {
              setForm(EMPTY);
              setApplied({ form: EMPTY, offset: 0 });
            }}
          >
            Reset
          </button>
        </div>
      </form>

      <div className="card">
        {results.loading && <Loading label="Running screen..." />}
        {results.error && <ErrorBox error={results.error} onRetry={results.reload} />}
        {!results.loading && !results.error && results.data && results.data.items.length === 0 && (
          <Empty>No instruments match these filters. Instruments without stored indicators or fundamentals are excluded when a filter needs them.</Empty>
        )}
        {!results.loading && !results.error && results.data && results.data.items.length > 0 && (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th>Ticker</th>
                  <th>Name</th>
                  <th>Sector</th>
                  <th className="right">Market cap</th>
                  <th className="right">Close</th>
                  <th className="right">SMA 200</th>
                  <th className="right">RSI</th>
                  <th className="right">ADX</th>
                </tr>
              </thead>
              <tbody>
                {results.data.items.map((r) => (
                  <tr key={r.instrumentId}>
                    <td>
                      <Link to={`/chart?instrument=${r.instrumentId}`}>{r.ticker}</Link>
                    </td>
                    <td>{r.name}</td>
                    <td className="muted">{r.sector ?? ""}</td>
                    <td className="right num">{fmtCompact(r.marketCap)}</td>
                    <td className="right num">{r.close ?? "n/a"}</td>
                    <td className="right num">{fmtNum(r.sma200)}</td>
                    <td className="right num">{fmtNum(r.rsi, 1)}</td>
                    <td className="right num">{fmtNum(r.adx, 1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="row between">
              <button
                type="button"
                className="btn small"
                disabled={applied.offset === 0}
                onClick={() => setApplied({ ...applied, offset: Math.max(0, applied.offset - PAGE) })}
              >
                Previous
              </button>
              <span className="muted">
                Rows {applied.offset + 1} to {applied.offset + results.data.items.length}
              </span>
              <button
                type="button"
                className="btn small"
                disabled={results.data.nextOffset === null}
                onClick={() => setApplied({ ...applied, offset: results.data!.nextOffset! })}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
